/**
 * Route-level UI state (design.md §7 "state/" module list + §3 "State
 * restoration contract"). Two concerns, both intentionally NOT the query
 * cache:
 *
 * 1. `buildAllowedSearchParams` — allow-listed query-param propagation
 *    between related routes, the same open-redirect defense shape as
 *    `api/client.ts#buildSafeReturnState` (PR2 review fix): only a
 *    caller-declared allow-list of keys is ever carried across a route
 *    boundary, never an arbitrary `returnTo`.
 *
 *    **Scope clarification (Jose Luis Arcila's explicit decision, PR #94
 *    review): this is INFRASTRUCTURE ONLY in PR5 — it has ZERO real
 *    callers today.** It is tested in isolation (`routeState.test.ts`) but
 *    is not wired into any existing screen's filter/pagination state. In
 *    particular, "Back/Forward filter restoration" is NOT functionally live
 *    anywhere in the panel yet: no screen reads a query string built by
 *    this helper on mount, so there is nothing for a browser Back/Forward
 *    press to actually restore through it. Wiring a real filter-bearing
 *    screen (e.g. `CampaignsScreen`) to this helper is a later, separately
 *    scoped change — same deferred-wiring pattern as
 *    `useUnsavedChangesBlocker` below (a real mechanism, with zero real
 *    callers in this PR).
 *
 * 2. `saveRouteUiState`/`restoreRouteUiState`/`useRouteScrollRestoration` —
 *    scroll position + non-shareable disclosure state, persisted to
 *    `sessionStorage` ONLY, keyed by `pathname + search` (design.md §3 point 3).
 *    This is UI restoration metadata, never query-cache data — the cache
 *    itself stays memory-only and is cleared on logout (design.md §7,
 *    `state/queryClient.ts#clearAllQueries`).
 *
 *    **This part IS functionally live**: `useRouteScrollRestoration` is
 *    wired into every route today via `app/RouteFocus.tsx`, which every
 *    route renders inside `AppShell`'s `<main>`. Scroll position genuinely
 *    saves/restores on real Back/Forward navigation; only the query-param
 *    allow-list (item 1 above) is the infra-only, unwired half of this
 *    module.
 *
 * This module also owns the unsaved-editor navigation blocker (task 5.1/5.2):
 * PR5 builds the blocking MECHANISM here; individual editor screens opt in
 * by calling `useUnsavedChangesBlocker` with their own dirty flag in a later
 * slice (PR9 campaign editor, PR15 template editor). Like the query-param
 * allow-list above, this has zero real callers in this PR — it is a tested,
 * ready mechanism, not a functionally-delivered feature yet.
 */

import { useEffect, useRef } from "react";
import type { NavigationType } from "react-router-dom";

/** Non-shareable UI state for one `pathname+search` key — never form content, never PII. */
export interface RouteUiState {
  readonly scrollY: number;
  readonly disclosure: Readonly<Record<string, boolean>>;
}

export const ROUTE_STATE_STORAGE_PREFIX = "panel-route-state:";

function storageKeyFor(pathnameAndSearch: string): string {
  return `${ROUTE_STATE_STORAGE_PREFIX}${pathnameAndSearch}`;
}

/**
 * Extracts only the caller-declared allow-listed keys from a route's current
 * search string — used to build a child route's query string when linking
 * from a list to its detail/monitoring/telemetry destinations (design.md §3
 * point 2: "Only an allow-list of known keys is accepted; never accept an
 * arbitrary returnTo URL").
 */
export function buildAllowedSearchParams(search: string, allowedKeys: readonly string[]): URLSearchParams {
  const source = new URLSearchParams(search);
  const result = new URLSearchParams();
  for (const key of allowedKeys) {
    const value = source.get(key);
    if (value !== null) {
      result.set(key, value);
    }
  }
  return result;
}

/** Persists scroll/disclosure metadata for one `pathname+search` key to `sessionStorage` only. */
export function saveRouteUiState(pathnameAndSearch: string, state: RouteUiState): void {
  window.sessionStorage.setItem(storageKeyFor(pathnameAndSearch), JSON.stringify(state));
}

/** Reads back previously saved scroll/disclosure metadata for a `pathname+search` key, or `undefined` if none was saved. */
export function restoreRouteUiState(pathnameAndSearch: string): RouteUiState | undefined {
  const raw = window.sessionStorage.getItem(storageKeyFor(pathnameAndSearch));
  if (raw === null) {
    return undefined;
  }
  try {
    return JSON.parse(raw) as RouteUiState;
  } catch {
    return undefined;
  }
}

/** A confirmed-leave check plus a native `beforeunload` guard — see `useUnsavedChangesBlocker` below. */
export interface UnsavedChangesGuard {
  /**
   * Call this BEFORE performing a programmatic in-app navigation (e.g.
   * `if (confirmLeave()) navigate(target)`). Returns `true` immediately when
   * there is nothing unsaved; otherwise shows a native confirmation and
   * returns the user's choice. Never autosaves or persists the caller's
   * dirty content anywhere — confirmation only.
   */
  confirmLeave: () => boolean;
}

/**
 * Unsaved-editor navigation blocking mechanism (task 5.1/5.2). PR5 builds
 * this MECHANISM; a later slice (PR9 campaign editor, PR15 template editor)
 * wires an actual dirty flag from a real form and calls `confirmLeave()`
 * before navigating.
 *
 * Two layers, deliberately router-library-agnostic:
 * 1. `beforeunload`: blocks tab close/refresh/typed-URL navigation with the
 *    browser's own native "leave site?" prompt — this is unconditionally
 *    correct and requires no router coupling.
 * 2. `confirmLeave()`: an explicit check editor screens call before firing a
 *    programmatic `navigate(...)` for an in-app route change, showing the
 *    same native `window.confirm` dialog design.md §3 point 4 calls for.
 *
 * Deliberately does NOT depend on React Router's `useBlocker` (which
 * requires a data router created via `createBrowserRouter`). This panel
 * keeps the existing declarative `<BrowserRouter>`/`<Routes>` shape (task
 * 5.2) for the shell — confirmed by research during
 * redesign-panel-precision-logistics that this repo's Vitest+jsdom+Node
 * toolchain crashes on ANY completed data-router navigation (a genuine
 * jsdom/undici `AbortSignal` class-identity mismatch), so migrating to
 * `useBlocker` would break every navigation test in this repo.
 *
 * redesign-panel-precision-logistics (#194): extends the mechanism so
 * `AppShell.tsx`'s primary/bottom-bar navigation and the browser Back
 * button are ALSO guarded, not just `beforeunload` and each editor's own
 * explicit `confirmLeave()` call. Two additions, both router-agnostic:
 *
 * 3. A module-level guard STACK (`confirmActiveNavigation` checks its TOP):
 *    every mounted `useUnsavedChangesBlocker(true)` instance pushes itself
 *    on mount and removes ITSELF by identity on cleanup — not "clear the
 *    slot if it still looks like me" (finding R3-001: that could silently
 *    drop protection if two guards mount at once, e.g. a dirty modal over
 *    an already-dirty screen, and unmount out of order). `AppShell.tsx`
 *    calls `confirmActiveNavigation()` in `NavLink`/bottom-bar `onClick`
 *    and `preventDefault()`s the click when it returns `false`.
 * 4. A "shadow history entry" for the browser Back button, pushed ONCE per
 *    MOUNT (not once per `shouldBlock` true-transition — native review
 *    finding R3-002 caught that a component toggling dirty->clean->dirty
 *    while staying mounted would otherwise push a fresh shadow entry every
 *    time) via a `useRef` latch. Back consumes the TOP guard's shadow entry
 *    first — the visible route never changes and `popstate` fires without
 *    React Router seeing a real navigation — so the confirm can run before
 *    anything is actually left. With guards stacked (a dirty modal over an
 *    already-dirty parent, each having pushed its own shadow entry), EVERY
 *    stacked guard is asked, top to bottom, before anything is consumed
 *    (native review finding, CRITICAL: the previous version only asked the
 *    top guard, then blindly consumed the NEXT guard's still-unconfirmed
 *    shadow entry via a raw `history.back()`, silently discarding that
 *    screen's unsaved changes with no prompt). Only once every guard agrees
 *    does `history.go(-guardStack.length)` consume all of their shadow
 *    entries plus the real route entry beneath them, in one traversal —
 *    `go()` fires exactly one resulting `popstate` regardless of how many
 *    entries it skips. Declining at any layer re-arms a fresh shadow entry
 *    for the TOP guard (the only one whose entry the real Back press
 *    actually consumed) so the next Back press is caught too.
 *    `bypassNextPopState` (time-bounded via `armPopStateBypass`, see its own
 *    doc comment) exists only to skip that single self-triggered `popstate`
 *    from the confirmed `history.go()` call.
 *
 *    Disclosed, accepted limitation (native review finding R3-002, only
 *    partially fixable without a data router): confirming departure via a
 *    `NavLink` click (not Back) leaves that mount's shadow entry orphaned
 *    one level below the newly-pushed route, since nothing can remove a
 *    non-top history entry — the browser History API only lets you
 *    navigate through entries, never delete one out of the middle of the
 *    stack. The user needs one extra (harmless — same URL as the real
 *    previous page, no wrong content, no data loss) Back press to fully
 *    clear it. A full fix needs router-level navigation interception
 *    (`useBlocker`), already ruled out elsewhere in this file for breaking
 *    this repo's navigation test suite.
 */
interface GuardStackEntry {
  readonly guard: UnsavedChangesGuard;
  readonly urlAtActivation: string;
}

const guardStack: GuardStackEntry[] = [];
let bypassNextPopState = false;
let bypassTimeoutId: ReturnType<typeof setTimeout> | undefined;

function topGuardEntry(): GuardStackEntry | undefined {
  return guardStack[guardStack.length - 1];
}

/**
 * Arms the self-triggered-popstate bypass for at most `BYPASS_WINDOW_MS`
 * (native review finding R3-2, CRITICAL, correcting the previous round's
 * disclosed-but-unbounded version): a real browser's popstate resulting
 * from `history.back()` is a separate, later task, not synchronous, so a
 * genuinely concurrent second Back/Forward press could in principle arrive
 * first and get incorrectly swallowed. Two independent problems, one fix:
 * (1) that race window shrinks from unbounded to `BYPASS_WINDOW_MS`, far
 * below any realistic human double-press interval; (2) more importantly,
 * an expected popstate that never arrives at all (`history.back()` is a
 * silent no-op at the start of history) no longer leaves the flag stuck
 * `true` forever, which would otherwise swallow EVERY future confirm
 * check permanently — a strictly worse bug than the race itself.
 */
const BYPASS_WINDOW_MS = 500;

function armPopStateBypass(): void {
  bypassNextPopState = true;
  if (bypassTimeoutId !== undefined) {
    clearTimeout(bypassTimeoutId);
  }
  bypassTimeoutId = setTimeout(() => {
    bypassNextPopState = false;
    bypassTimeoutId = undefined;
  }, BYPASS_WINDOW_MS);
}

function disarmPopStateBypass(): void {
  bypassNextPopState = false;
  if (bypassTimeoutId !== undefined) {
    clearTimeout(bypassTimeoutId);
    bypassTimeoutId = undefined;
  }
}

/**
 * Checked by `AppShell.tsx` before an in-app navigation (nav-link click).
 * True when there is nothing to block, or every stacked guard confirmed
 * leaving.
 *
 * native review finding (CRITICAL, correction round 2): originally checked
 * only the TOP guard, unlike `handleGuardedPopState` below which — after its
 * own CRITICAL fix — consults every stacked guard. With guards nested (a
 * dirty modal over an already-dirty parent screen), clicking a NavLink let
 * the inner guard's confirmation alone navigate away, silently discarding
 * the outer screen's unsaved changes with no prompt — the exact same bug
 * class the popstate path was fixed for, left open on this path. Now asks
 * every guard, top to bottom, short-circuiting on the first decline.
 */
export function confirmActiveNavigation(): boolean {
  for (let i = guardStack.length - 1; i >= 0; i--) {
    if (!guardStack[i]!.guard.confirmLeave()) {
      return false;
    }
  }
  return true;
}

/**
 * native review finding R3-... (CRITICAL): with two+ guards stacked (a dirty
 * modal over an already-dirty parent screen), each mounted instance pushed
 * its OWN shadow entry, but this handler only ever asked the TOP guard
 * before blindly calling `history.back()` once more — silently consuming
 * the NEXT guard's still-unconfirmed shadow entry (and, with 3+ guards, more
 * beyond that) with no prompt, discarding that screen's unsaved changes.
 *
 * Fix: ask EVERY currently-stacked guard, top (most recently mounted) to
 * bottom, synchronously in this same handler call — `confirmLeave()` is a
 * blocking `window.confirm()`, so there is no need to round-trip through a
 * separate popstate per layer. Only once every guard agrees do we finish
 * leaving, consuming all of their shadow entries plus the real route entry
 * beneath them in ONE `history.go(-N)` traversal: per the History API,
 * `go()` fires exactly one resulting `popstate` for the final destination
 * regardless of how many entries it skips (unlike calling `back()` N times,
 * which would fire N sequential events this handler would have to
 * separately bypass). `armPopStateBypass()` only ever needs to swallow that
 * single resulting event, whether N is 1 (the original single-guard case)
 * or more.
 */
function handleGuardedPopState(): void {
  if (bypassNextPopState) {
    disarmPopStateBypass();
    return;
  }
  const top = topGuardEntry();
  if (top === undefined) {
    return;
  }
  for (let i = guardStack.length - 1; i >= 0; i--) {
    if (!guardStack[i]!.guard.confirmLeave()) {
      // Re-arm: only the TOP guard's shadow entry was actually consumed by
      // the real Back press that triggered this handler — guards below the
      // decliner were never touched, their own shadow entries are still
      // intact further down in history.
      window.history.pushState(null, "", top.urlAtActivation);
      return;
    }
  }
  armPopStateBypass();
  window.history.go(-guardStack.length);
}

if (typeof window !== "undefined") {
  window.addEventListener("popstate", handleGuardedPopState);
}

/**
 * epic #236 tarea #241, revision automatica de Codex sobre PR #255 (P2,
 * confirmado real, "Block navigation instead of merely confirming in-flight
 * writes"): `confirmLeave()` originalmente SOLO confirmaba -- si el operador
 * aceptaba "salir sin guardar" mientras un POST/PUT seguia en vuelo, la
 * navegacion procedia igual, desmontando el componente. La escritura en si
 * NUNCA se cancelaba: un POST exitoso terminaba creando el borrador sin
 * ningun aviso visible (y sin refetch de la lista, habilitando un
 * reintento duplicado); un PUT exitoso o fallido se volvia invisible. A
 * diferencia de una edicion sin guardar (que SI es seguro descartar),
 * "hay un request de red en vuelo" no es descartable por una confirmacion
 * del operador -- ni el ni nosotros podemos deshacer que ya salio.
 * `hardBlock` (opcional, default `false`, aditivo -- ningun llamador
 * existente pasa un segundo argumento) bloquea la navegacion SIN mostrar
 * ningun confirm mientras sea `true`: ni "Cerrar" via boton local, ni un
 * link de navegacion primaria/mobile, ni Back del navegador puede
 * desmontar el componente hasta que el request en vuelo termine (exito o
 * error) y `hardBlock` vuelva a `false`.
 */
export function useUnsavedChangesBlocker(shouldBlock: boolean, hardBlock = false): UnsavedChangesGuard {
  // `hardBlock` leido via ref (mismo patron que `dirtyRef` en
  // AudienceFilterEditor.tsx): el efecto de abajo solo se re-ejecuta (y
  // solo entonces vuelve a empujar la entrada en `guardStack`) cuando
  // `shouldBlock` TRANSICIONA -- si `hardBlock` cambia mientras
  // `shouldBlock` ya era `true` (p.ej. una edicion sin guardar que luego
  // empieza a guardarse, sin que `shouldBlock` deje de ser `true` en
  // ningun momento), la entrada ya empujada en el stack seguiria cerrando
  // sobre el valor VIEJO de `hardBlock` si se leyera directo del
  // parametro. El ref siempre tiene el valor MAS RECIENTE, sin importar
  // cuando corrio el efecto por ultima vez.
  const hardBlockRef = useRef(hardBlock);
  hardBlockRef.current = hardBlock;

  const guard: UnsavedChangesGuard = {
    confirmLeave: () => {
      if (!shouldBlock) return true;
      if (hardBlockRef.current) return false;
      return window.confirm("Hay cambios sin guardar. ¿Deseas salir sin guardarlos?");
    },
  };
  const shadowPushedForThisMountRef = useRef(false);

  useEffect(() => {
    if (!shouldBlock) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    const entry: GuardStackEntry = { guard, urlAtActivation: window.location.href };
    guardStack.push(entry);

    if (!shadowPushedForThisMountRef.current) {
      shadowPushedForThisMountRef.current = true;
      window.history.pushState(null, "", window.location.href);
    }

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      const index = guardStack.indexOf(entry);
      if (index !== -1) {
        guardStack.splice(index, 1);
      }
    };
    // `guard` is intentionally omitted: it is a fresh object every render
    // (see confirmLeave's closure above), so only a real `shouldBlock`
    // transition should re-run this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldBlock]);

  return guard;
}

/**
 * Scroll restoration keyed by `pathname+search` (design.md §3 point 3). On a
 * `POP` (Back/Forward) navigation to a route with saved metadata, restores
 * its scroll position; every other navigation (`PUSH`/`REPLACE`, or `POP` to
 * a route never visited) scrolls to top instead — matching design.md §3
 * point 5 "Browser Back restores list focus... then scroll". The outgoing
 * route's current scroll position is saved on the way out, before the next
 * route's restore/reset runs.
 *
 * Review fix P2 (Jose Luis Arcila, PR #94): a synchronous restore inside the
 * mount effect can under-scroll if the destination screen's content loads
 * asynchronously (e.g. via `useQuery`) and grows the page taller AFTER the
 * restore already ran — the browser clamps a nonzero `scrollTo` to whatever
 * height exists at that instant, and nothing re-attempted it once real
 * content arrived. Two bounded mitigations, not a general
 * wait-for-network-idle mechanism:
 * 1. The restore is deferred past two nested `requestAnimationFrame` calls,
 *    letting the initial synchronous paint settle before scrolling.
 * 2. When there IS a nonzero target to restore, a `ResizeObserver` on
 *    `#main-content` re-applies that same target once per resize while this
 *    route is mounted — scoped to this effect's own lifetime (it
 *    disconnects on cleanup/route change, it never runs indefinitely, and
 *    it is skipped entirely for a scroll-to-top target since there is
 *    nothing to guard against re-clamping to `0`).
 */
export function useRouteScrollRestoration(
  routeKey: string,
  navigationType: NavigationType,
  containerId = "main-content",
): void {
  useEffect(() => {
    const savedScrollY = navigationType === "POP" ? restoreRouteUiState(routeKey)?.scrollY : undefined;
    const targetScrollY = savedScrollY ?? 0;

    let secondFrameId: number | undefined;
    const firstFrameId = requestAnimationFrame(() => {
      secondFrameId = requestAnimationFrame(() => {
        window.scrollTo(0, targetScrollY);
      });
    });

    let resizeObserver: ResizeObserver | undefined;
    if (targetScrollY > 0 && typeof ResizeObserver !== "undefined") {
      const container = document.getElementById(containerId);
      if (container) {
        resizeObserver = new ResizeObserver(() => {
          window.scrollTo(0, targetScrollY);
        });
        resizeObserver.observe(container);
      }
    }

    return () => {
      cancelAnimationFrame(firstFrameId);
      if (secondFrameId !== undefined) {
        cancelAnimationFrame(secondFrameId);
      }
      resizeObserver?.disconnect();

      const existing = restoreRouteUiState(routeKey);
      saveRouteUiState(routeKey, { scrollY: window.scrollY, disclosure: existing?.disclosure ?? {} });
    };
  }, [routeKey, navigationType, containerId]);
}
